import { Injectable, Optional, Inject, OnDestroy } from '@angular/core';
import { forkJoin, fromEvent, of, take, filter, tap, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { SessionManagementService } from '../../../Bootstrap/services/session-management.service';
import { WebsocketAwareNeptuneService } from '../../../Bootstrap/services/websocket-aware-neptune.service';

import { environment } from '../../../../environments/environment';
import { UserStateModel } from '../../../Core/Users/models/user-state.model';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { PendoEvent } from '../models/pendo-event';
import { version, gitHash } from '../../../../environments/version';
import { UserStateProvider } from '../../../Core/Users/interface/user-state-provider.interface';
import { MonitorConfigService } from '../../MonitorShared/services/monitor-config.service';
import { BriefingConfigService } from '../../MonitorShared/services/briefing-config.service';

@Injectable()
export class PendoService implements OnDestroy {

    pendo: pendo.Pendo = pendo;

    exludeEmails: string[] = ['qa.automation@agilitypr.com'];
    state: UserStateModel;
    isInIframe: boolean = window.self !== window.top;
    appVersion: string = version + '-' + gitHash;
    iframeParentName: string = null;
    private subscriptions: Subscription[] = [];

    // currently we only want to gather data for QA, so I only added the pendoApiKey to QA's environment files
    constructor(
        @Optional() @Inject('UserStateProvider') private currentUserStateService: UserStateProvider,
        @Optional() private monitorConfigService: MonitorConfigService,
        @Optional() private briefingConfigService: BriefingConfigService,
        @Optional() private sessionManagementService: SessionManagementService,
        @Optional() private websocketAwareNeptuneService: WebsocketAwareNeptuneService
    ) {
        if (environment.pendoApiKey) {
            // initialize pendo with the api key from the environment file
            // doing this we don't need the script in the index.html
            this.pendo.initialize({
                apiKey: environment.pendoApiKey
            });
        }

        if (this.isInIframe) {
            try {
                this.iframeParentName = window.top?.document?.referrer || null;
            } catch (err) {
                // Nothing to do, blocked by Cross-origin
            }
        }

        if (this.sessionManagementService) {
            this.subscriptions.push(
                this.sessionManagementService.sessionStarted$.subscribe(({ reason }) => {
                    this.trackEvent({
                        type: 'Session Started',
                        data: {
                            reason,
                            tabSessionId: this.websocketAwareNeptuneService?.tabSessionId,
                            timestamp: new Date().toISOString(),
                        }
                    });
                }),
                this.sessionManagementService.sessionWarningShown$.subscribe(() => {
                    this.trackEvent({
                        type: 'Session Expiry Warning Shown',
                        data: {
                            tabSessionId: this.websocketAwareNeptuneService?.tabSessionId,
                            timestamp: new Date().toISOString(),
                        }
                    });
                }),
                this.sessionManagementService.userSessionExpired$.subscribe(({ performLogout }) => {
                    this.trackEvent({
                        type: 'Session Forced Logout',
                        data: {
                            reason: performLogout ? 'frontend_inactivity' : 'other_tab_logout',
                            tabSessionId: this.websocketAwareNeptuneService?.tabSessionId,
                            timestamp: new Date().toISOString(),
                        }
                    });
                }),
            );
        }

        this.subscriptions.push(
            this.currentUserStateService?.getState().pipe(
                filter(state => {
                    return !!state && this.canIdentifyPendoState(state);
                }),
                take(1),
                switchMap(state => {
                    if (state.account.permissions.includes(userPermissionsEnum.advancedMonitoring)) {
                        return forkJoin({
                            userState: of(state),
                            monitorConfig: this.monitorConfigService?.getConfig().pipe(
                                filter(x => !!x),
                                take(1)
                            ),
                            briefingConfig: this.briefingConfigService?.getConfig().pipe(
                                filter(x => !!x),
                                take(1)
                            )
                        });
                    } else {
                        return of({ userState: state, monitorConfig: null, briefingConfig: null });
                    }
                })
            ).subscribe(({ userState, monitorConfig, briefingConfig }) => {
                const state = this.getPendoState(userState);
                if (monitorConfig && briefingConfig) {
                    state.account.hasCoverageInsights = !!monitorConfig.hasCoverageInsights;
                    state.account.hasMentionInsights = !!monitorConfig.hasMentionInsights;
                    state.account.hasBriefingsAutogenerateInsights = !!briefingConfig.insightAutoGenerationBriefings;
                }

                this.pendo.identify(state);
            }),
            // visibilitychange:hidden fires on tab close, navigation, window close, and tab backgrounding.
            fromEvent(document, 'visibilitychange').pipe(
                filter(() => document.visibilityState === 'hidden')
            ).subscribe(() => {
                this.trackEvent({
                    type: 'Tab Hidden',
                    data: {
                        tabSessionId: this.websocketAwareNeptuneService?.tabSessionId,
                        timestamp: new Date().toISOString(),
                        pathname: window.location.pathname,
                    }
                });
            })
        );

    }

    getPendoState(state: UserStateModel) {
        let user = state?.user;
        const account = state?.account;

        if (state?.impersonator?.user) {
            user = state.impersonator.user;
        }
        const identify = {
            visitor: {
                id: user?.id + '',
                email: user?.email,
                username: user?.username,
                firstName: user?.firstName,
                lastName: user?.lastName,
                internal: user?.internal + '',
                isAdmin: user?.permissions?.includes(userPermissionsEnum.accountManager),
                appVersion: this.appVersion,
                isInIframe: this.isInIframe,
                iframeParentName: this.iframeParentName
            },
            account: {
                id: account?.id + '',
                accountName: account?.name,
                internal: account?.internal,
                hasMonitoring: account?.permissions?.includes(userPermissionsEnum.advancedMonitoring),
                hasTargeting: account?.permissions?.includes(userPermissionsEnum.advancedTargeting),
                hasSocial: account?.permissions?.includes(userPermissionsEnum.socialIntelligence),
                hasAI: account?.permissions?.includes(userPermissionsEnum.openAI),
                hasAgent: account?.permissions?.includes(userPermissionsEnum.agenticFeature),
                partnerResellerId: account?.partnerResellerId ? account?.partnerResellerId + '' : '',
                startDate: account?.startDate?.toDate()?.toISOString(),
                expiryDate: account?.expiryDate?.toDate()?.toISOString(),
                hasCoverageInsights: false,
                hasMentionInsights: false,
                hasBriefingsAutogenerateInsights: false
            }
        };

        if (environment.pendoPrefix) {
            identify.visitor.id = environment.pendoPrefix + identify.visitor.id;
            identify.account.id = environment.pendoPrefix + identify.account.id;
        }

        return identify;
    }

    canIdentifyPendoState(state: UserStateModel): boolean {
        return environment.pendoApiKey && !this.exludeEmails.includes(state?.user?.email);
    }

    trackButtonPress(id: string) {
        if (environment.pendoApiKey) {
            this.pendo.track('Button Pressed', {
                buttonId: id,
                pathname: window.location.pathname,
            });
        }
    }

    trackEvent(event: PendoEvent) {
        if (environment.pendoApiKey) {
            this.pendo.track(event.type, event.data);
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
